import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { api } from '../services/api';
import API_URL from '../config/api';

const styles = {
    modal: {
        maxWidth: '1300px',
        height: 'min(85vh, 980px)',
        maxHeight: 'min(85vh, 980px)',
    },
    bodyContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        flex: 1,
        overflow: 'auto',
        position: 'relative',
        minHeight: 0,
    },
    dashboardPanel: {
        flex: '1 1 280px',
        maxWidth: '340px',
        minWidth: '260px',
        borderRight: '1px solid var(--line-subtle)',
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        paddingTop: 'var(--space-2)',
    },
    dashboardSection: {
        padding: 'var(--space-4) var(--space-5)',
        borderBottom: '1px solid var(--line-subtle)',
    },
    dashboardTitle: {
        fontSize: '0.72rem',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'var(--text-tertiary)',
        marginBottom: 'var(--space-3)',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
    },
    // Clean pathology header
    pathologyHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        marginBottom: 'var(--space-2)',
    },
    pathologyText: {
        fontSize: '0.95rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
    },

    // Prominent alert cards
    alertCard: {
        backgroundColor: 'var(--danger-soft)',
        border: '1px solid rgba(240, 86, 79, 0.3)',
        borderRadius: 'var(--radius-md)',
        padding: '12px 14px',
        marginBottom: 'var(--space-2)',
    },
    alertTitle: {
        color: 'var(--danger)',
        fontWeight: 600,
        fontSize: '0.9rem',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        marginBottom: '4px',
        letterSpacing: '0.01em',
    },
    warningCard: {
        backgroundColor: 'var(--warning-soft)',
        border: '1px solid rgba(245, 184, 61, 0.3)',
        borderRadius: 'var(--radius-md)',
        padding: '12px 14px',
        marginBottom: 'var(--space-2)',
    },
    warningTitle: {
        color: 'var(--warning)',
        fontWeight: 600,
        fontSize: '0.9rem',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        marginBottom: '4px',
        letterSpacing: '0.01em',
    },
    pathologyDetailText: {
        color: 'var(--text-primary)',
        fontSize: '0.88rem',
        lineHeight: 1.45,
    },

    // X-ray metrics grid
    metricsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: 'var(--space-2)',
    },
    metricCard: {
        background: 'var(--bg-overlay)',
        padding: '10px 12px',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--line-subtle)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    metricLabel: {
        fontSize: '0.72rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        color: 'var(--text-secondary)',
        marginBottom: '2px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    metricValue: {
        fontSize: '1.05rem',
        color: 'var(--text-primary)',
        fontWeight: 600,
        fontFamily: 'var(--font-mono)',
        fontVariantNumeric: 'tabular-nums',
        display: 'flex',
        alignItems: 'baseline',
        gap: '2px',
    },
    metricUnit: {
        fontSize: '0.7rem',
        color: 'var(--text-tertiary)',
        fontWeight: 400,
        fontFamily: 'inherit',
        marginLeft: '2px',
    },

    // CT anatomy cards
    ctLevelCard: {
        padding: '10px 12px',
        background: 'var(--bg-overlay)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--line-subtle)',
        marginBottom: 'var(--space-2)',
    },
    ctHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: '6px',
        borderBottom: '1px solid var(--line-subtle)',
        paddingBottom: '4px',
    },
    ctLevelTitle: {
        color: 'var(--accent)',
        fontWeight: 600,
        fontSize: '0.88rem',
    },
    ctDetailRow: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '0.8rem',
        color: 'var(--text-secondary)',
        marginBottom: '2px',
    },

    contentPanel: {
        flex: '3 1 480px',
        minWidth: 0,
        padding: 'clamp(24px, 4vw, 52px)',
        overflowY: 'auto',
        color: 'var(--text-primary)',
        lineHeight: 1.7,
        fontSize: '0.95rem',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 0',
        gap: 'var(--space-4)',
        width: '100%',
        height: '100%',
        color: 'var(--text-tertiary)',
        fontSize: '0.9rem',
    },
    statusDot: {
        width: '8px',
        height: '8px',
        borderRadius: 'var(--radius-full)',
        display: 'inline-block',
        flexShrink: 0,
    },
    tag: {
        display: 'inline-block',
        padding: '1px 7px',
        borderRadius: 'var(--radius-full)',
        fontSize: '0.65rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        marginLeft: '6px',
        verticalAlign: 'middle',
        fontFamily: 'var(--font-sans, inherit)',
    },
    severity: {
        Mild: { backgroundColor: 'var(--success-soft)', color: 'var(--success)' },
        Moderate: { backgroundColor: 'var(--warning-soft)', color: 'var(--warning)' },
        Severe: { backgroundColor: 'var(--danger-soft)', color: 'var(--danger)' },
    },
    timestamp: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        color: 'var(--text-tertiary)',
        fontSize: '0.75rem',
        fontVariantNumeric: 'tabular-nums',
    },
    // Markdown typography
    mdH1: {
        fontSize: '1.35rem',
        fontWeight: 600,
        letterSpacing: '-0.02em',
        color: 'var(--text-primary)',
        marginBottom: '24px',
    },
    mdH2: {
        fontSize: '1.08rem',
        fontWeight: 600,
        letterSpacing: '-0.01em',
        color: 'var(--text-primary)',
        marginTop: '32px',
        marginBottom: '12px',
        borderBottom: '1px solid var(--line-default)',
        paddingBottom: '8px',
    },
    mdH3: {
        fontSize: '0.95rem',
        color: 'var(--text-secondary)',
        marginTop: '20px',
        marginBottom: '8px',
        fontWeight: 600,
    },
    mdLi: { marginBottom: '6px', marginLeft: '20px' },
    mdP: { marginBottom: '16px', color: 'var(--text-secondary)' },
    mdStrong: { color: 'var(--accent)', fontWeight: 600 },
};

// Compact metric card for the alignment grid
const MetricCard = ({ label, value, unit, severity }) => (
    <div style={styles.metricCard}>
        <div style={styles.metricLabel} title={label}>{label}</div>
        <div style={styles.metricValue}>
            {value}
            {unit && <span style={styles.metricUnit}>{unit}</span>}
            {severity && styles.severity[severity] && (
                <span style={{ ...styles.tag, ...styles.severity[severity] }}>
                    {severity}
                </span>
            )}
        </div>
    </div>
);

const SectionHeader = ({ title, icon }) => (
    <div style={styles.dashboardTitle}>
        {icon}
        {title}
    </div>
);

const AiSummaryModal = ({ isOpen, onClose }) => {
    const [summary, setSummary] = useState(null);
    const [clinicalData, setClinicalData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [timestamp, setTimestamp] = useState(null);
    const closeButtonRef = useRef(null);

    // Fetch summary when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchSummary(false);
        }
    }, [isOpen]);

    // Move initial focus to the close button when the modal opens
    useEffect(() => {
        if (isOpen && closeButtonRef.current) {
            closeButtonRef.current.focus();
        }
    }, [isOpen]);

    // Close on Escape while open
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    const fetchSummary = async (forceRefresh = false) => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.post(`${API_URL}/inference/ai-summary`, null, {
                params: { force_refresh: forceRefresh }
            });

            if (response.data) {
                if (response.data.error) {
                    setError(response.data.summary || response.data.message || "An error occurred");
                } else if (response.data.summary) {
                    setSummary(response.data.summary);
                    setTimestamp(response.data.timestamp);
                    setClinicalData(response.data.data || null);
                }
            }
        } catch (err) {
            console.error("Error fetching AI summary:", err);
            setError(err.response?.data?.detail || "Failed to connect to AI service. Please ensure backend is running.");
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    // Helper to get XRay params safely
    const getXRayParam = (paramName) => {
        if (!clinicalData?.xray_parameters) return null;

        // Search in lat or ap sections
        const lat = clinicalData.xray_parameters.lat || {};
        const ap = clinicalData.xray_parameters.ap || {};
        const combined = clinicalData.xray_parameters.combined || {};

        return lat[paramName] || ap[paramName] || combined[paramName];
    };

    const renderPathologyDashboard = () => {
        if (!clinicalData?.pathology) return null;

        const { stenosis, location, type, herniation } = clinicalData.pathology;
        const hasPathology = stenosis || (herniation && herniation.present);

        // If no pathology, show clean "All Clear" status
        if (!hasPathology) {
            return (
                <div style={styles.dashboardSection}>
                    <div style={styles.pathologyHeader}>
                        <div className="status-dot" style={{ ...styles.statusDot, backgroundColor: 'var(--success)' }}></div>
                        <div style={{ ...styles.pathologyText, color: 'var(--success)' }}>
                            No Significant Pathology
                        </div>
                    </div>
                </div>
            );
        }

        // If pathology exists, show PROMINENT alerts
        return (
            <div style={styles.dashboardSection}>
                {stenosis && (
                    <div style={styles.alertCard}>
                        <div style={styles.alertTitle}>
                            <div style={{ ...styles.statusDot, backgroundColor: 'var(--danger)' }}></div>
                            Stenosis Detected
                        </div>
                        <div style={styles.pathologyDetailText}>
                            <strong>{type}</strong> at {location}
                        </div>
                    </div>
                )}

                {herniation && herniation.present && (
                    <div style={styles.warningCard}>
                        <div style={styles.warningTitle}>
                            <div style={{ ...styles.statusDot, backgroundColor: 'var(--warning)' }}></div>
                            Disc Herniation
                        </div>
                        <div style={styles.pathologyDetailText}>
                            <strong>{herniation.type}</strong> at {herniation.location}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderXRayDashboard = () => {
        if (!clinicalData?.xray_parameters) return null;

        const cobb = getXRayParam('cobb_angle');
        const sva = getXRayParam('sva');
        const pi = getXRayParam('pelvic_incidence');
        const pt = getXRayParam('pelvic_tilt');
        const ll = getXRayParam('lumbar_lordosis');
        const mismatch = getXRayParam('pi_ll_mismatch');

        return (
            <div style={styles.dashboardSection}>
                <SectionHeader
                    title="X-Ray Alignment"
                    icon={
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 3v18M8 21h8M3 10h18" />
                        </svg>
                    }
                />
                <div style={styles.metricsGrid}>
                    {cobb && <MetricCard label="Cobb Angle" value={cobb.value} unit="°" severity={cobb.severity} />}
                    {sva && <MetricCard label="S.V.A." value={sva.value} unit={sva.unit} />}
                    {mismatch && <MetricCard label="PI-LL Mismatch" value={mismatch.value} unit="°" severity={Math.abs(mismatch.value) > 10 ? 'Severe' : 'Mild'} />}
                    {pi && <MetricCard label="Pelvic Incidence" value={pi.value} unit="°" />}
                    {pt && <MetricCard label="Pelvic Tilt" value={pt.value} unit="°" />}
                    {ll && <MetricCard label="Lumbar Lordosis" value={ll.value} unit="°" />}
                </div>
            </div>
        );
    };

    return (
        <div
            className="modal-scrim"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="modal" style={styles.modal} role="dialog" aria-modal="true">
                {/* Header */}
                <div className="modal__header">
                    <div className="modal__title" style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--accent)' }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        AI Surgical Assistant
                    </div>
                    <button
                        ref={closeButtonRef}
                        type="button"
                        className="btn btn--ghost btn--sm"
                        style={{ fontSize: '1.2rem', lineHeight: 1, padding: '0 10px' }}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        &times;
                    </button>
                </div>

                {/* Body */}
                <div style={styles.bodyContainer}>
                    {loading ? (
                        <div style={styles.loadingContainer}>
                            <div className="spinner"></div>
                            <div>Analyzing CT, MRI, and X-Ray data...</div>
                        </div>
                    ) : error ? (
                        <div style={{ width: '100%', padding: 'var(--space-5)' }}>
                            <div className="alert alert--danger">
                                <strong>Error:</strong> {error}
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Left Panel: Clinical Data Dashboard */}
                            {clinicalData && (
                                <div style={styles.dashboardPanel}>

                                    {/* 1. Pathology Status (Clean Header) */}
                                    {renderPathologyDashboard()}

                                    {/* 2. Alignment (Clean List) */}
                                    {renderXRayDashboard()}

                                    {/* 3. Anatomy (Clean List) */}
                                    <div style={styles.dashboardSection}>
                                        <SectionHeader
                                            title="CT Anatomy"
                                            icon={
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <rect x="2" y="2" width="20" height="20" rx="4" />
                                                    <path d="M8 6h8M8 12h8M8 18h8" />
                                                </svg>
                                            }
                                        />

                                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                            {clinicalData.ct_measurements?.length > 0 ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                                    {clinicalData.ct_measurements.slice(0, 4).map((m, i) => (
                                                        <div key={i} style={styles.ctLevelCard}>
                                                            <div style={styles.ctHeader}>
                                                                <span style={styles.ctLevelTitle}>{m.level || 'Unknown'}</span>
                                                                <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>VH: {m.vertebralHeightAnt}mm</span>
                                                            </div>
                                                            <div style={styles.ctDetailRow}>
                                                                <span>Pedicle (R/L)</span>
                                                                <span style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{m.pedicleWidthR} / {m.pedicleWidthL} mm</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {clinicalData.ct_measurements.length > 4 && (
                                                        <div style={{ textAlign: 'center', marginTop: 'var(--space-2)', fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                                                            + {clinicalData.ct_measurements.length - 4} more levels
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div style={{ fontStyle: 'italic', color: 'var(--text-tertiary)' }}>No CT data available.</div>
                                            )}
                                        </div>
                                    </div>

                                </div>
                            )}

                            {/* Right Panel: AI Analysis - Clean Typography */}
                            <div style={styles.contentPanel}>
                                <div className="markdown-body" style={{ color: 'var(--text-primary)' }}>
                                    <ReactMarkdown
                                        components={{
                                            h1: ({ node, children, ...props }) => <h1 style={styles.mdH1} {...props}>{children}</h1>,
                                            h2: ({ node, children, ...props }) => <h2 style={styles.mdH2} {...props}>{children}</h2>,
                                            h3: ({ node, children, ...props }) => <h3 style={styles.mdH3} {...props}>{children}</h3>,
                                            li: ({ node, children, ...props }) => <li style={styles.mdLi} {...props}>{children}</li>,
                                            p: ({ node, children, ...props }) => <p style={styles.mdP} {...props}>{children}</p>,
                                            strong: ({ node, ...props }) => <strong style={styles.mdStrong} {...props} />,
                                        }}
                                    >
                                        {summary}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="modal__footer">
                    {timestamp && (
                        <div style={styles.timestamp}>
                            {new Date(timestamp).toLocaleString()}
                        </div>
                    )}
                    <button
                        type="button"
                        className="btn btn--secondary"
                        onClick={() => fetchSummary(true)}
                        disabled={loading}
                    >
                        Regenerate
                    </button>
                    <button
                        type="button"
                        className="btn btn--primary"
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AiSummaryModal;
